import { getPurchaseOrder, PurchaseOrderDetail, PurchaseOrderLine } from './PurchasingService';
import { findTicketByRef } from './TicketsService';
import { listClients } from './ClientService';
import { getWarehouseId } from './TerminalService';
import { centroDe } from './TicketPrintService';
import { PrintCompany, PrintOrder, PrintOrderLine, renderOrderHtml } from '../utils/documentTemplates';
import { printHtml } from '../utils/printHtml';

/**
 * Resguardo del pedido para el cliente: la orden de compra, pero vista desde el
 * lado del cliente que paga.
 *
 * Dos cosas que no son evidentes:
 *
 * 1. Se filtra por cliente. Una orden de compra va a un PROVEEDOR y puede
 *    juntar lineas de varios clientes (createRestockOrder reaprovecha el ultimo
 *    borrador del proveedor cuando el backend no devuelve id, y la reposicion
 *    de tienda usa el mismo camino). Imprimir la orden entera le ensenaria a un
 *    cliente lo que ha pedido otro.
 *
 * 2. Los importes salen del ticket, no de la orden. Las lineas de pedido se
 *    crean con unit_price 0,00 (el catalogo solo tiene precio de venta y el de
 *    coste lo pone compras al aprobar), asi que la orden vale 0 EUR. El precio
 *    que le interesa al cliente es el que ya pago, y ese esta en el ticket que
 *    las notas de la orden guardan: "Pedido de cliente - Ticket T1-81".
 */

/** "Pedido de cliente - Ticket T1-81" -> "T1-81". */
const ticketRefDeNotas = (notes: string): string => {
    const match = /ticket\s+([A-Za-z0-9-]+)/i.exec(notes || '');
    return match ? match[1] : '';
};

const companyDe = (): PrintCompany => {
    const centro = centroDe(getWarehouseId());
    return {
        name: centro.razonSocial,
        cif: centro.nif,
        address: `${centro.direccion} ${centro.poblacion}`.trim(),
        // Los datos del centro ya traen el "Tel:" delante; companyBlock pone el suyo.
        phone: centro.telefono.replace(/^tel[:.]?\s*/i, ''),
        email: centro.email
    };
};

/**
 * La linea de la orden puede venir con el titulo del catalogo (book_title) y el
 * ticket con el que tenia el carrito: se comparan sin mayusculas ni espacios de
 * sobra para que un "El Quijote " y un "EL QUIJOTE" sigan cruzando.
 */
const claveTitulo = (title: string): string => title.trim().toUpperCase().replace(/\s+/g, ' ');

/**
 * Precios del ticket de origen, por titulo. Por titulo y no por id de libro
 * porque las lineas de la orden no guardan el id del libro del carrito.
 */
const preciosDelTicket = async (notes: string): Promise<{ ref: string; precios: Record<string, number> }> => {
    const ref = ticketRefDeNotas(notes);
    if (!ref) return { ref: '', precios: {} };

    try {
        const ticket = await findTicketByRef(ref);
        if (!ticket) return { ref, precios: {} };

        const precios: Record<string, number> = {};
        (ticket.items || []).forEach(item => {
            // El descuento de linea ya aplicado: es lo que el cliente pago.
            precios[claveTitulo(item.title)] = item.price * (1 - (item.discount || 0) / 100);
        });
        return { ref, precios };
    } catch {
        // El ticket puede ser de otro dia o de otra caja: el resguardo sale
        // igual, solo que sin importes.
        return { ref, precios: {} };
    }
};

/** Lineas de la orden que son de este cliente. Sin cliente, no hay resguardo. */
const lineasDe = (order: PurchaseOrderDetail, clientName: string): PurchaseOrderLine[] =>
    order.lines.filter(l => l.clientName === clientName);

export const loadCustomerOrder = async (
    orderId: string,
    clientName: string,
    statusLabel: string
): Promise<PrintOrder> => {
    const order = await getPurchaseOrder(orderId);
    const propias = lineasDe(order, clientName);
    if (propias.length === 0) {
        throw new Error(`El pedido ${order.number} no tiene líneas de ${clientName}.`);
    }

    const { ref, precios } = await preciosDelTicket(order.notes);
    const hasPrices = propias.every(l => precios[claveTitulo(l.title)] !== undefined);

    const lines: PrintOrderLine[] = propias.map(l => {
        const unitPrice = precios[claveTitulo(l.title)] || 0;
        return {
            title: l.title,
            quantity: l.quantityOrdered,
            received: l.quantityReceived,
            unitPrice,
            lineTotal: unitPrice * l.quantityOrdered
        };
    });

    return {
        number: order.number,
        createdAt: order.createdAt,
        statusLabel,
        clientName,
        supplierName: order.supplierName,
        expectedDelivery: order.expectedDelivery,
        ticketNumber: ref,
        hasPrices,
        total: lines.reduce((sum, l) => sum + l.lineTotal, 0),
        lines,
        company: companyDe()
    };
};

/** El mismo HTML que se imprime, para enseñarlo en pantalla. */
export const customerOrderHtml = async (
    orderId: string,
    clientName: string,
    statusLabel: string
): Promise<string> => renderOrderHtml(await loadCustomerOrder(orderId, clientName, statusLabel));

export const printCustomerOrder = async (
    orderId: string,
    clientName: string,
    statusLabel: string
): Promise<void> => {
    await printHtml(renderOrderHtml(await loadCustomerOrder(orderId, clientName, statusLabel)));
};

/**
 * Email que el cliente tiene en su ficha, '' si no tiene o no se encuentra.
 *
 * El pedido solo guarda el NOMBRE del cliente, asi que hay que buscarlo en el
 * listado. Con dos clientes del mismo nombre no hay forma de distinguir: se
 * devuelve '' y la direccion se teclea a mano. La factura ya no hace este
 * rodeo (invoices_get trae la ficha del cliente por SQL); si algun dia el
 * pedido guarda el client_id, aqui se puede quitar igual.
 */
export const clientEmailDe = async (clientName: string): Promise<string> => {
    try {
        if (!clientName) return '';
        const found = await listClients(clientName);
        const exactos = found.filter(c => c.companyName.trim().toUpperCase() === clientName.trim().toUpperCase());
        return exactos.length === 1 ? (exactos[0].email || '') : '';
    } catch {
        return '';
    }
};
